// UserAuthServices.jsx
import { useState } from "react";
import API from "../../api/apiUser";
import { useNotification } from "../../context/NotificationContext";

export function useUserAuth() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();

  const _extract = (res) => (res?.data ? res.data : res);

  // === USER LOGIN ===
  const userLogin = async (email, password, nextPath = null) => {
    setLoading(true);
    setError(null);
    try {
      const payload = { email, password };
      if (nextPath) payload.next = nextPath;

      const res = await API.post("/auth/user/login", payload);
      const data = _extract(res);

      if (data.status === "failed") {
        setError(data.message);
        showNotification({
          type: "failed",
          title: "Login Failed",
          message: data.message,
        });
        return data;
      }

      if (data.access_token) {
        localStorage.setItem("user_token", data.access_token);
      }

      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
      }

      showNotification({
        type: "success",
        title: "Login Successful",
        message: `Welcome back, ${data.user?.full_name || "User"}!`,
      });

      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Network error or server unavailable.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Login Failed",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };

  const userLogout = async (navigate) => {
    try {
      await API.post("/auth/user/logout");
    } catch {
      console.warn("⚠️ User logout backend call failed silently");
    } finally {
      localStorage.removeItem("user");
      localStorage.removeItem("user_token");
      setUser(null);
      if (navigate) navigate("/login");
    }
  };

  const forgotPasswordAPI = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/user/forgot-password", { email });
      const data = _extract(res);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to request code.";
      setError(msg);
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpAPI = async (email, otp) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/user/verify-otp", { email, otp });
      const data = _extract(res);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Invalid or expired code.";
      setError(msg);
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordAPI = async (email, token, password) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/user/reset-password", { email, token, password });
      const data = _extract(res);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to reset password.";
      setError(msg);
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (passwordData) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/user/change-password", passwordData);
      const data = _extract(res);

      if (data.status === "success") {
        showNotification({
          type: "success",
          title: "Success",
          message: data.message || "Password updated successfully."
        });
      } else {
        showNotification({
          type: "failed",
          title: "Update Failed",
          message: data.message || "Failed to update password."
        });
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to change password.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Error",
        message: msg
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  return {
    user,
    setUser,
    loading,
    error,
    userLogin,
    userLogout,
    clearError,
    forgotPasswordAPI,
    verifyOtpAPI,
    resetPasswordAPI,
    changePassword,
  };
}
